import { Link } from "react-router-dom";

import styled from "styled-components/macro";

import NewSignupButton from "Signups/SharedComponents/NewSignupButton";
import { useSignups } from "Signups/state";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import FnrIcon, { FnrIconWrapper } from "UI/FnrIcon";

export default function Intro({ show }) {
  const [state] = useSignups();

  const {
    canViewFamilyRegistrationAdminPanel,
    familyRegistrationIsLive,
    hasFundIt,
    hasSignupPayments,
    hasFNP,
    allSignupLists,
    user,
  } = state;

  const isSuperAdmin = user?.isSuperAdmin;

  if (!show) return null;

  let signupsTitle = "Simple Signups with Payments";
  let signupsDescription =
    "Payments via Fund It are included in your subscription.";
  if (!hasFundIt)
    signupsDescription =
      "Payments via Fund It are included in your subscription but Fund It must be enabled. Transaction fees apply. Learn more about Fund It.";

  if (!hasSignupPayments) {
    signupsTitle = "Simple Signups";
    signupsDescription =
      "Whether you're planning a potluck or a retreat, use Signups to help your members Signup and get the info they need.";
  }

  let familyRegDescription =
    "Allow whole families to register themselves as a household or create a connection card for your visitors that they can fill out online!";

  if (!hasFNP)
    familyRegDescription += " *Requires an upgrade to Flocknote People.";

  const signupListsHolderIsEmpty =
    !allSignupLists || allSignupLists?.length === 0;

  return (
    <ScrollabaleContainer signupListsHolderIsEmpty={signupListsHolderIsEmpty}>
      <Wrapper>
        <Section>
          <TitleIcon>
            <FnrIcon fad fa-clipboard-list-check />
          </TitleIcon>
          <Title>{signupsTitle}</Title>

          <Subtext>
            <SignupsDescription
              hasFundIt={hasFundIt}
              hasSignupPayments={hasSignupPayments}
              isSuperAdmin={isSuperAdmin}
            />
          </Subtext>
          <HighlightsContainer>
            <Highlight>
              <FnrIcon
                fad
                fa-hand-heart
                style={{
                  "--fa-primary-color": "#f2d3f8",
                  "--fa-secondary-color": "#ff638f",
                }}
              />
              Schedule Volunteers
            </Highlight>
            {hasSignupPayments && (
              <Highlight>
                <FnrIcon
                  fad
                  fa-ticket
                  style={{
                    "--fa-primary-color": "#C56006",
                    "--fa-secondary-color": "#FEC524",
                  }}
                />
                Sell tickets to an event
              </Highlight>
            )}
            <Highlight>
              <FnrIcon
                fad
                fa-calendar-check
                style={{
                  "--fa-primary-color": "#ccaded",
                  "--fa-secondary-color": "#8B169C",
                }}
              />
              Register for classes or events
            </Highlight>
            {!hasSignupPayments && (
              <Highlight>
                <FnrIcon
                  fad
                  fa-envelope
                  style={{
                    "--fa-primary-color": "#C56006",
                    "--fa-secondary-color": "#FEC524",
                  }}
                />
                Link groups to follow up
              </Highlight>
            )}
          </HighlightsContainer>

          <NewSignupButton />
        </Section>
        {familyRegistrationIsLive && canViewFamilyRegistrationAdminPanel && (
          <Section>
            <TitleIcon>
              <FnrIcon fad fa-address-book />
            </TitleIcon>
            <Title>Family & Visitor Registration</Title>

            <Subtext>{familyRegDescription}</Subtext>
            <HighlightsContainer>
              <Highlight>
                <FnrIcon
                  fad
                  fa-edit
                  style={{
                    "--fa-primary-color": "#016099",
                    "--fa-secondary-color": "#828C8D",
                  }}
                />
                Customizable
              </Highlight>
              <Highlight>
                <FnrIcon
                  fad
                  fa-home
                  style={{
                    "--fa-primary-color": "#C56006",
                    "--fa-secondary-color": "#E87E04",
                  }}
                />
                Group members into households
              </Highlight>
              <Highlight>
                <FnrIcon
                  fad
                  fa-hand-holding-usd
                  style={{
                    "--fa-primary-color": "#24CE7B",
                    "--fa-secondary-color": "#FEC524",
                  }}
                />
                Prompt members to set up online giving
              </Highlight>
            </HighlightsContainer>

            {canViewFamilyRegistrationAdminPanel && (
              <SLink to="/signups/family-registration">
                <FnrButton
                  color="blue"
                  size="large"
                  data-cy="introManageFamilyRegistrationBtn"
                >
                  <ButtonIcon fad fa-address-book />
                  <ButtonText style={{ fontSize: 19 }}>
                    Manage Family & Visitor Registration
                  </ButtonText>
                </FnrButton>
              </SLink>
            )}
          </Section>
        )}
      </Wrapper>
    </ScrollabaleContainer>
  );
}

const ScrollabaleContainer = styled.div`
  width: ${({ signupListsHolderIsEmpty }) =>
    signupListsHolderIsEmpty ? "100%" : "calc(100% - 300px)"};
  margin-left: ${({ signupListsHolderIsEmpty }) =>
    signupListsHolderIsEmpty ? "0" : "300px"};
  min-height: 100vh;
  box-sizing: border-box;
  overflow: auto;
`;
const Wrapper = styled.div`
  display: flex;
  font-weight: 400;
  align-items: center;
  justify-content: space-evenly;
  min-height: 100%;

  @media screen and (max-width: 1390px) {
    flex-direction: column;
  }
`;
const Section = styled.div`
  padding: 30px;
  box-sizing: border-box;
  width: 50%;
  flex-grow: 0;
  flex-shrink: 0;
  max-width: 540px;
  @media screen and (max-width: 1390px) {
    width: 100%;
  }
`;
const TitleIcon = styled.div`
  color: #278dc1;
  font-size: 52px;
`;
const Title = styled.div`
  font-size: 28px;
`;
const Subtext = styled.div`
  font-size: 18px;
  color: #828c8d;
  margin-bottom: 24px;
  margin-top: 8px;
`;
const HighlightsContainer = styled.div`
  margin-bottom: 24px;
`;
const Highlight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  & ${FnrIconWrapper} {
    font-size: 36px;
    width: 42px;
    text-align: center;
    flex-shrink: 0;
    --fa-secondary-opacity: 1;
  }
`;

const SignupsDescription = ({ hasFundIt, hasSignupPayments, isSuperAdmin }) => {
  if (!hasSignupPayments) {
    return (
      <>
        {
          "Whether you're planning a potluck or a retreat, use Signups to help your members Signup and get the info they need."
        }
      </>
    );
  }

  if (!hasFundIt) {
    const handleClick = () => {
      if (isSuperAdmin) {
        window.fnReactBus.goToUrl("/fundit");
      } else {
        window.open("https://flocknote.com/fund-it/", "_blank");
      }
    };

    return (
      <>
        {
          "Secure payment processing is included with all Flocknote plans, but must be enabled first. "
        }{" "}
        <LinkSpan onClick={handleClick}>Learn more</LinkSpan>.
      </>
    );
  }

  return <>{"Secure payment processing is included in your subscription."}</>;
};

const LinkSpan = styled.span`
  color: #278dc1;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const SLink = styled(Link)`
  text-decoration: none !important;
  
  &:hover, &:focus, &:active {
    text-decoration: none !important;
  }
`

